/**
 * RetrospectiveNotesCard Component
 *
 * Displays organization-level retrospective notes with @mention support
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { Notes as NotesIcon, Add, Delete, Edit, Person, Groups, Visibility } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { EmptyState } from '../shared/EmptyState';
import { okrApi } from '../../../services/okrApi';

// Regex to find @mentions in format @[Name](type:id)
const MENTION_REGEX = /@\[([^\]]+)\]\((user|team):([^)]+)\)/g;

// Render note content with mentions as styled chips
const renderNoteContent = (content: string, isDarkMode: boolean): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(MENTION_REGEX.source, 'g');

    while ((match = regex.exec(content)) !== null) {
        // Add text before mention
        if (match.index > lastIndex) {
            parts.push(
                <span key={`text-${lastIndex}`}>
                    {content.substring(lastIndex, match.index)}
                </span>
            );
        }
        // Add mention chip
        const mentionType = match[2] as 'user' | 'team';
        parts.push(
            <Chip
                key={`${match[2]}-${match[3]}-${match.index}`}
                icon={mentionType === 'user' ? <Person fontSize="small" /> : <Groups fontSize="small" />}
                label={match[1]}
                size="small"
                sx={{
                    mx: 0.5,
                    my: 0.25,
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: mentionType === 'user'
                        ? (isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)')
                        : (isDarkMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)'),
                    color: mentionType === 'user'
                        ? (isDarkMode ? '#60a5fa' : '#2563eb')
                        : (isDarkMode ? '#a78bfa' : '#7c3aed'),
                    border: `1px solid ${mentionType === 'user'
                        ? (isDarkMode ? '#3b82f680' : '#3b82f640')
                        : (isDarkMode ? '#8b5cf680' : '#8b5cf640')}`,
                    '& .MuiChip-icon': {
                        color: 'inherit',
                        fontSize: '0.875rem',
                    },
                }}
            />
        );
        lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
        parts.push(
            <span key={`text-${lastIndex}`}>
                {content.substring(lastIndex)}
            </span>
        );
    }

    return parts.length > 0 ? parts : [content];
};

export const RetrospectiveNotesCard: React.FC = () => {
    const { isDarkMode, data, permissions, sections, dialogs } = useOKRDashboardContext();
    const { notes, setSuccess, setError, refetchData, userContext } = data;
    const { expandedSections, toggleSection } = sections;
    const [viewNote, setViewNote] = useState<any>(null);

    // Get current user ID for author comparison
    const currentUserId = userContext?.id;

    const handleDeleteNote = async (noteId: string) => {
        try {
            await okrApi.deleteNote(noteId);
            setSuccess('Note deleted successfully');
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to delete note');
        }
    };

    const handleEditNote = (note: any) => {
        dialogs.openNoteDialog({ id: note.id, content: note.content });
    };

    return (
        <CollapsibleCard
            icon={<NotesIcon sx={{ color: '#fff', fontSize: 24 }} />}
            iconBgGradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            title="Retrospective Notes"
            subtitle="Organization-level insights and learnings"
            expanded={expandedSections.notes}
            onToggle={() => toggleSection('notes')}
            isDarkMode={isDarkMode}
            count={notes.length}
            actions={
                permissions.canAddNotes && (
                    <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={(e) => {
                            e.stopPropagation();
                            dialogs.openNoteDialog();
                        }}
                        sx={{
                            bgcolor: 'rgba(245, 158, 11, 0.15)',
                            color: '#f59e0b',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(245, 158, 11, 0.25)',
                            },
                        }}
                    >
                        Add Note
                    </Button>
                )
            }
        >
            {notes.length === 0 ? (
                <EmptyState
                    title="No Notes Yet"
                    description="Add retrospective notes to capture insights and learnings"
                    isDarkMode={isDarkMode}
                    compact
                />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {notes.map((note) => (
                        <Box
                            key={note.id}
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: isDarkMode ? '#1e293b40' : '#f8fafc',
                                border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Box
                                        onClick={() => setViewNote(note)}
                                        sx={{
                                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                            fontSize: '0.9rem',
                                            lineHeight: 1.8,
                                            mb: 1.5,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                                borderRadius: 1,
                                            },
                                        }}
                                    >
                                        {renderNoteContent(note.content, isDarkMode)}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {note.author_name || 'Unknown'}
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: 3,
                                                height: 3,
                                                borderRadius: '50%',
                                                bgcolor: isDarkMode ? '#475569' : '#cbd5e1',
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: isDarkMode ? '#64748b' : '#94a3b8',
                                            }}
                                        >
                                            {new Date(note.created_at).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="View Full Note">
                                        <IconButton
                                            size="small"
                                            onClick={() => setViewNote(note)}
                                            sx={{
                                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                                '&:hover': {
                                                    bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                },
                                            }}
                                        >
                                            <Visibility fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {note.created_by === currentUserId && (
                                        <>
                                            <Tooltip title="Edit Note">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditNote(note)}
                                                    sx={{
                                                        color: isDarkMode ? '#94a3b8' : '#64748b',
                                                        '&:hover': {
                                                            bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                        },
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Note">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteNote(note.id)}
                                                    sx={{
                                                        color: '#ef4444',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(239, 68, 68, 0.1)',
                                                        },
                                                    }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* View Note Dialog */}
            <Dialog
                open={!!viewNote}
                onClose={() => setViewNote(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                        borderRadius: 3,
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <NotesIcon sx={{ color: '#fff', fontSize: 24 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Retrospective Note
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                By {viewNote?.author_name || 'Unknown'} • {viewNote && new Date(viewNote.created_at).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            color: isDarkMode ? '#f1f5f9' : '#1e293b',
                            fontSize: '1rem',
                            lineHeight: 1.8,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            py: 2,
                        }}
                    >
                        {viewNote && renderNoteContent(viewNote.content, isDarkMode)}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1 }}>
                    <Button
                        onClick={() => setViewNote(null)}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 3,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </CollapsibleCard>
    );
};

export default RetrospectiveNotesCard;
